import React from "react";
import { reportClientError } from "@/lib/error-reporter";

interface Props {
  children: React.ReactNode;
}
interface State {
  hasError: boolean;
}

/**
 * Root error boundary. Catches render/lifecycle errors React swallows from the
 * global window handlers, reports them (with the component stack), and shows a
 * self-contained fallback.
 *
 * It sits ABOVE the app's providers (in main.tsx), so the fallback must not use
 * any hooks, context, or UI-kit components — hence the inline styles.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    reportClientError({
      type: "react",
      message: error?.message || "React render error",
      stack: error?.stack ?? null,
      componentStack: info?.componentStack ?? null,
    });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          padding: "2rem",
          textAlign: "center",
          background: "#0a0a0f",
          color: "#e5e7eb",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <h1 style={{ fontSize: "1.25rem", fontWeight: 700, margin: 0 }}>Something went wrong</h1>
        <p style={{ color: "#9ca3af", maxWidth: "28rem", margin: 0, lineHeight: 1.5 }}>
          An unexpected error occurred and the page couldn't continue. The issue has been reported.
          Try reloading.
        </p>
        <button
          onClick={this.handleReload}
          style={{
            marginTop: "0.5rem",
            padding: "0.5rem 1.25rem",
            borderRadius: "0.5rem",
            border: "none",
            background: "#f59e0b",
            color: "#111827",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
